import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import GeoEventReportsMap from 'components/GeoEventReportsMap';
import { StructuredEvacuationsEditLayer } from 'components/Map/layers/StructuredEvacuationsLayer';
import {
  EvacZoneStatus,
  FormEvacZoneStatus,
  LayerEvacZone,
} from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useRegionEvacZonesForGeoEvent } from 'features/evacZones/hooks/useRegionEvacZonesForGeoEvent';
import { useFormGeoEvent } from 'components/MediaForm';
import { AlertCamerasLayer } from 'components/Map/layers/AlertCamerasLayer';
import { FormValues } from './types';

type ReportMapProps = {
  geoEvent: Wildfire;
  zoomControlEnabled: boolean;
};

export const ReportMap = (props: ReportMapProps): JSX.Element => {
  const { geoEvent, zoomControlEnabled } = props;
  const { setValue, watch } = useFormContext<FormValues>();

  const mapGeoEvent = useFormGeoEvent({ geoEvent });

  const { regionEvacZones } = useRegionEvacZonesForGeoEvent(geoEvent);

  const [
    media,
    temporarilyDisplayEvacZones,
    activeEvacuations,
    evacZoneStatuses,
  ] = watch([
    'media',
    'temporarilyDisplayEvacZones',
    'activeEvacuations',
    'evacZoneStatuses',
  ]);

  const handleEvacZoneClick = useCallback(
    (uid: string, nextStatus?: EvacZoneStatus) => {
      if (!geoEvent) return;

      const evacZone = regionEvacZones.find((zone) => zone.uidV2 === uid);

      if (!evacZone) return;

      const filteredCurrentZones = evacZoneStatuses.filter(
        (eZS) => eZS.evacZone.uidV2 !== uid,
      );

      const geoEventEvacZone = geoEvent.evacZoneStatuses.find(
        (eZS) => eZS.evacZone.uidV2 === uid,
      );

      if (nextStatus) {
        // Add
        const updatedZone: FormEvacZoneStatus = {
          evacZone: {
            ...evacZone,
            regionEvacZoneStyle: evacZone.region.evacZoneStyle,
          },
          status: nextStatus,
        };
        if (geoEventEvacZone) {
          updatedZone.prevStatus = geoEventEvacZone.status;
        }
        setValue('evacZoneStatuses', [...filteredCurrentZones, updatedZone]);
        return;
      }
      // Remove
      setValue('evacZoneStatuses', filteredCurrentZones);
    },
    [regionEvacZones, evacZoneStatuses, geoEvent, setValue],
  );

  const evacZones: LayerEvacZone[] = useMemo(() => {
    const filteredZones = regionEvacZones.filter(
      (zone) =>
        !evacZoneStatuses.find((eZS) => eZS.evacZone.uidV2 === zone.uidV2),
    );
    return [
      ...filteredZones.map((zone) => ({
        uidV2: zone.uidV2,
        style: zone.region.evacZoneStyle,
      })),
      ...evacZoneStatuses.map((eZS) => ({
        uidV2: eZS.evacZone.uidV2,
        status: eZS.status,
        style: eZS.evacZone.regionEvacZoneStyle,
      })),
    ];
  }, [regionEvacZones, evacZoneStatuses]);

  return (
    <GeoEventReportsMap
      geoEvent={mapGeoEvent}
      hiddenMapPixels={null}
      noControls
      layersControlEnabled
      zoomControlEnabled={zoomControlEnabled}
    >
      <StructuredEvacuationsEditLayer
        visible={evacZones.length > 0 && activeEvacuations}
        evacZones={evacZones}
        onClick={temporarilyDisplayEvacZones ? undefined : handleEvacZoneClick}
      />
      <AlertCamerasLayer
        visible
        interactive={false}
        withLabels
        withRealtimeUpdates={false}
        // assume 'provider' key means this is a camera since we don't have
        // a better way to explicitly check the type
        selectedCameraId={media && 'provider' in media ? media.id : undefined}
      />
    </GeoEventReportsMap>
  );
};
